import type { UserData } from "~/domains/userData";
import { type Translations } from "~/services/translations/getTranslationByKey";
import { translations as staticTranslations } from "~/services/translations/translations";
import { parseCurrencyStringDE } from "~/services/validation/money/formatCents";
import {
  extractFieldItemsFromInlineItems,
  getItemValueBox,
} from "./getItemValueBox";
import { hasMoneyValidationSchema, hasNonEmptyLongTextField } from "./helper";
import type { FieldItems, SummaryOverviewBoxItemType } from "./types";

type Props = SummaryOverviewBoxItemType & {
  readonly userData: UserData;
  readonly translations: Translations;
  readonly pathname: string;
  readonly elementId?: string;
};

const SCROLLABLE_BOX_ROWS = 10;

const isValueNumber = (value: string): boolean => {
  return !Number.isNaN(parseCurrencyStringDE(value));
};

const SummaryValueOverflowContainer = ({
  children,
  fieldItems,
}: {
  children: React.ReactNode;
  fieldItems: FieldItems;
}) => {
  const useScrollableContainer = hasNonEmptyLongTextField(fieldItems);

  if (!useScrollableContainer) {
    return <>{children}</>;
  }

  return (
    <div className="kern-form-input">
      <div
        className="kern-form-input__input pt-kern-space-small! overflow-auto resize-y box-border whitespace-pre-wrap"
        style={{ blockSize: `${SCROLLABLE_BOX_ROWS * 1.5}rem` }}
      >
        {children}
      </div>
    </div>
  );
};

const SummaryOverviewBoxItem = ({
  userData,
  translations,
  title,
  inlineItems,
  pathname,
  elementId,
}: Props) => {
  const rawItemValue = getItemValueBox(translations, userData, inlineItems);
  if (rawItemValue.trim() === "") return null;

  const fieldItems = extractFieldItemsFromInlineItems(userData, inlineItems);
  const shouldAppendEuroWord = hasMoneyValidationSchema(pathname, fieldItems);
  const itemValue =
    shouldAppendEuroWord && isValueNumber(rawItemValue)
      ? `${rawItemValue} ${staticTranslations.currency.euro.de}`
      : rawItemValue;

  return (
    <>
      {title && (
        <dt
          data-testid="summary-box-item-title"
          className="kern-label text-kern-static-medium w-full"
          id={elementId}
        >
          {title}
        </dt>
      )}

      <dd
        data-testid="summary-box-item-value"
        className="kern-body text-kern-static-medium w-full h-full"
      >
        <SummaryValueOverflowContainer fieldItems={fieldItems}>
          {itemValue}
        </SummaryValueOverflowContainer>
      </dd>
    </>
  );
};

export default SummaryOverviewBoxItem;
